
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface CreateRoomParams {
  mentorId: string;
  menteeId: string;
  bookingId: string;
}

interface SessionData {
  booking_id: string;
  room_url: string;
  room_name: string;
  exp: number;
  isExistingRoom?: boolean;
}

export const useDailyVideoCall = () => {
  const [isCreatingRoom, setIsCreatingRoom] = useState(false);
  const { toast } = useToast();

  const getCurrentUserName = async (): Promise<string> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return 'User';

      const { data: profile, error } = await supabase
        .from('profiles')
        .select('first_name, last_name')
        .eq('id', user.id)
        .single();

      if (error || !profile) {
        console.error('Error fetching user profile:', error);
        return 'User';
      }

      const fullName = `${profile.first_name || ''} ${profile.last_name || ''}`.trim();
      return fullName || 'User';
    } catch (error) {
      console.error('Error getting current user name:', error);
      return 'User';
    }
  };

  const findOrCreateVideoRoom = async ({ mentorId, menteeId, bookingId }: CreateRoomParams): Promise<SessionData | null> => {
    setIsCreatingRoom(true);
    
    try {
      console.log('Finding or creating Daily.co room for:', { mentorId, menteeId, bookingId });
      
      // First, check if there's already a session with a room for these participants
      const { data: existingSessions, error: searchError } = await supabase
        .from('sessions')
        .select('*')
        .eq('mentor_id', mentorId)
        .eq('mentee_id', menteeId)
        .not('room_url', 'is', null)
        .order('created_at', { ascending: false })
        .limit(1);

      if (searchError) {
        console.error('Error searching for existing sessions:', searchError);
      } else if (existingSessions && existingSessions.length > 0) {
        const existingSession = existingSessions[0];
        console.log('Found existing session with room:', existingSession);
        
        toast({
          title: "Joining Existing Call",
          description: "Opening the existing video call room.",
        });

        return {
          booking_id: bookingId,
          room_url: existingSession.room_url,
          room_name: existingSession.room_name,
          exp: Math.floor(Date.now() / 1000) + 7200, // 2 hours fallback
          isExistingRoom: true
        };
      }

      // No existing room found, create a new one
      console.log('No existing room found, creating new Daily.co room');
      
      const { data, error } = await supabase.functions.invoke('create-daily-room', {
        body: {
          booking_id: bookingId
        }
      });

      if (error) {
        console.error('Error creating Daily.co room:', error);
        toast({
          title: "Error",
          description: "Failed to create video room. Please try again.",
          variant: "destructive",
        });
        return null;
      }

      if (!data?.success) {
        console.error('Daily.co room creation failed:', data);
        toast({
          title: "Error", 
          description: data?.error || "Failed to create video room.",
          variant: "destructive",
        });
        return null;
      }

      console.log('Daily.co room created successfully:', data);
      
      toast({
        title: "Starting New Call",
        description: "Your video call room is ready.",
      });

      return {
        booking_id: data.booking_id,
        room_url: data.room_url,
        room_name: data.room_name,
        exp: data.exp,
        isExistingRoom: false
      };

    } catch (error) {
      console.error('Exception in findOrCreateVideoRoom:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
      return null;
    } finally {
      setIsCreatingRoom(false);
    }
  };

  // Keep the old method for backward compatibility - now requires bookingId
  const createVideoRoom = async ({ mentorId, menteeId, bookingId }: CreateRoomParams): Promise<SessionData | null> => {
    return findOrCreateVideoRoom({ mentorId, menteeId, bookingId });
  };

  const startVideoCall = async (roomUrl: string, onCallStart?: () => void) => {
    try {
      // Get the current user's name from their profile
      const userName = await getCurrentUserName();
      console.log('Starting video call with user name:', userName);
      
      // Add the userName as a URL parameter to the Daily.co room URL
      const urlWithUserName = `${roomUrl}?userName=${encodeURIComponent(userName)}`;
      
      // Notify that call is starting (for duration management)
      if (onCallStart) {
        onCallStart();
      }
      
      // Open the Daily.co room in a new window/tab with the user's name
      window.open(urlWithUserName, '_blank', 'width=1024,height=768');
    } catch (error) {
      console.error('Error starting video call with user name:', error);
      // Fallback to opening without user name
      window.open(roomUrl, '_blank', 'width=1024,height=768');
    }
  };

  const updateSessionStatus = async (sessionId: string, status: string) => {
    try {
      const updateData: any = { status };
      
      if (status === 'active') {
        updateData.started_at = new Date().toISOString();
      } else if (status === 'completed') {
        updateData.ended_at = new Date().toISOString();
      }

      const { error } = await supabase
        .from('sessions')
        .update(updateData)
        .eq('id', sessionId);

      if (error) {
        console.error('Error updating session status:', error);
      } else {
        console.log(`Session ${sessionId} status updated to: ${status}`);
      }
    } catch (error) {
      console.error('Exception updating session status:', error);
    }
  };

  return {
    createVideoRoom,
    findOrCreateVideoRoom,
    startVideoCall,
    updateSessionStatus,
    isCreatingRoom
  };
};
